// API 配置
const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '127.0.0.1';

/**
 * 将 localhost 统一映射到 127.0.0.1，避免浏览器把 localhost 解析到 IPv6 ::1
 * 而后端 Daphne 只监听 IPv4 时导致 WebSocket handshake 被 reset。
 */
function normalizeLocalhost(hostname: string): string {
    return hostname === 'localhost' || hostname === '::1' ? '127.0.0.1' : hostname;
}

/**
 * 判断是否为本地/内网开发地址：开发环境直连 Daphne :8000；
 * 公网部署（经 Nginx 反代）使用同源相对路径 /api，避免把 :8000 暴露公网。
 */
function isDevHostname(hostname: string): boolean {
    return (
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname.startsWith('192.168.') ||
        hostname.startsWith('10.') ||
        hostname.startsWith('172.')
    );
}

const apiBaseUrl =
    process.env.REACT_APP_API_BASE_URL ||
    (typeof window !== 'undefined' && isDevHostname(currentHostname)
        ? `http://${normalizeLocalhost(currentHostname)}:8000/api`
        : '/api');

/**
 * 根据 HTTP API Base URL 推导 WebSocket Base URL。
 * 例如 http://127.0.0.1:8000/api -> ws://127.0.0.1:8000/ws
 * 相对路径 /api -> 按当前页面协议推导 ws(s)://<当前host>/ws
 */
function deriveWsUrl(apiUrl: string): string {
    if (typeof window !== 'undefined') {
        try {
            const url = new URL(apiUrl, window.location.origin);
            const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
            const host = normalizeLocalhost(url.hostname) + (url.port ? `:${url.port}` : '');
            return `${protocol}//${host}/ws`;
        } catch {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const port = window.location.port ? `:${window.location.port}` : '';
            return `${protocol}//${normalizeLocalhost(window.location.hostname)}${port}/ws`;
        }
    }
    return `ws://${normalizeLocalhost(currentHostname)}:8000/ws`;
}

/**
 * 支持 REACT_APP_WS_BASE_URL 为相对路径（如 /ws）：按当前页面协议转为绝对 ws(s) URL。
 */
function resolveWsBaseUrl(explicit: string | undefined, apiUrl: string): string {
    if (!explicit) return deriveWsUrl(apiUrl);
    if (/^wss?:\/\//.test(explicit)) return explicit;
    if (explicit.startsWith('/') && typeof window !== 'undefined') {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const port = window.location.port ? `:${window.location.port}` : '';
        return `${protocol}//${normalizeLocalhost(window.location.hostname)}${port}${explicit}`;
    }
    return explicit;
}

export const API_CONFIG = {
    // 动态拼接：如果是 localhost 访问就用 localhost，如果是 192.168.2.9 访问就用 192.168.2.9
    BASE_URL: apiBaseUrl,
    TIMEOUT: 120000,
    RETRY_ATTEMPTS: 3,
    RETRY_DELAY: 1000,
} as const;

export const WS_CONFIG = {
    // 允许通过 REACT_APP_WS_BASE_URL 显式覆盖（支持绝对 ws(s):// 或相对 /ws）；默认从 HTTP API URL 推导
    BASE_URL: resolveWsBaseUrl(process.env.REACT_APP_WS_BASE_URL, apiBaseUrl),
} as const;

// API 端点
export const API_ENDPOINTS = {
    // 认证相关 - JWT
    AUTH: {
        LOGIN: '/auth/login/',
        LOGOUT: '/auth/logout/',
        REFRESH: '/auth/refresh/',
        VALIDATE: '/auth/validate/',
        PROFILE: '/auth/profile/',
        CHANGE_PASSWORD: '/auth/change-password/',
        PASSWORD_RESET_REQUEST: '/auth/password-reset/request/',
        PASSWORD_RESET_CONFIRM: '/auth/password-reset/confirm/',
        // JWT Token endpoints (alternative)
        TOKEN: '/auth/token/',
        TOKEN_REFRESH: '/auth/token/refresh/',
    },

    // 聊天相关
    CHAT: {
        STREAM: '/chat/stream/',
        SESSIONS: '/chat-sessions/',
        SESSION_MESSAGES: (sessionId: string) => `/chat-sessions/${sessionId}/messages/`,
        // 2026-09-18 用户令：消息反馈（赞/踩/意见，沿用 Kevin Message.feedback 设施）
        MESSAGE_FEEDBACK: (messageId: string) => `/messages/${messageId}/feedback/`,
        MESSAGE_FEEDBACK_BY_CONTENT: '/messages/feedback-by-content/',
        DELETE_SESSION: (sessionId: string) => `/chat-sessions/${sessionId}/`,
    },

    // 文件相关
    FILES: {
        UPLOAD: '/uploaded-files/',
        BATCH_UPLOAD: '/uploaded-files/batch-upload/',   // 2026-09-17 多文件合并为一个案例
        STATUS: (fileId: string) => `/uploaded-files/${fileId}/status/`,
        PROCESS_NTRK: (fileId: string) => `/uploaded-files/${fileId}/process_ntrk/`,
        MANUAL_ENTRY: '/uploaded-files/manual-entry/',
    },
    VARIANT_CONFIRMATION: {
        PENDING_VARIANTS: (fileId: string) => `/genomic-variants/pending/${fileId}/`,
        CONFIRM_VARIANTS: (fileId: string) => `/genomic-variants/confirm/${fileId}/`,
        REFINE_VARIANTS: (fileId: string) => `/genomic-variants/refine/${fileId}/`,
    },
    // 系统配置
    CONFIG: {
        ACTIVE: '/configurations/active/',
        UPDATE: (id: string) => `/configurations/${id}/`,
        HEALTH: '/health/',
    },
} as const;